// pages/ImportSiteWeb.js
// Permet à un exposant de coller l'URL de son site et laisser l'IA créer ses annonces

import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import * as api from "../api";

const CATEGORIES_LABELS = {
  yachting:     "Le Yachting",
  moteur:       "Bateaux à Moteur",
  voile:        "La Voile",
  equipement:   "Équipement",
  sports:       "Sports et Loisirs",
  marinas:      "Les Marinas",
  navires:      "Navires Professionnels",
  construction: "Construction Navale",
};

function ImportSiteWeb() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [step,          setStep]          = useState("url");   // url | analyzing | preview | confirming | done
  const [url,           setUrl]           = useState("");
  const [error,         setError]         = useState("");
  const [analysisResult,setAnalysisResult]= useState(null);
  const [selectedIds,   setSelectedIds]   = useState([]);
  const [history,       setHistory]       = useState([]);
  const [histLoading,   setHistLoading]   = useState(false);
  const [createdCount,  setCreatedCount]  = useState(0);

  useEffect(() => {
    if (!user || (user.role !== "exposant" && user.role !== "admin")) {
      navigate("/connexion");
    }
  }, [user, navigate]);

  useEffect(() => {
    setHistLoading(true);
    api.websiteImportHistory()
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, [step]);

  // ── Étape 1 : analyser l'URL ──────────────────────────────────────────────
  const handleAnalyze = async () => {
    setError("");
    if (!url.trim()) { setError("Veuillez entrer une URL."); return; }
    if (!/^(https?:\/\/)?[\w-]+(\.[\w-]+)+/.test(url.trim())) {
      setError("URL invalide. Exemple : https://monentreprise.dz");
      return;
    }
    setStep("analyzing");
    try {
      const res = await api.websiteImportAnalyze({ url: url.trim() });
      setAnalysisResult(res.data);
      // Pré-sélectionner toutes les annonces
      setSelectedIds((res.data.annonces || []).map((_, i) => i));
      setStep("preview");
    } catch (e) {
      setError(e.response?.data?.message || "Erreur lors de l'analyse. Vérifiez l'URL.");
      setStep("url");
    }
  };

  // ── Étape 2 : confirmer les annonces sélectionnées ────────────────────────
  const handleConfirm = async () => {
    if (selectedIds.length === 0) { setError("Sélectionnez au moins une annonce."); return; }
    setStep("confirming");
    setError("");
    try {
      const annonces = selectedIds.map(i => analysisResult.annonces[i]);
      const res = await api.websiteImportConfirm({ url: url.trim(), annonces });
      setCreatedCount(res.data.annonces_creees);
      setStep("done");
    } catch (e) {
      setError(e.response?.data?.message || "Erreur lors de la création des annonces.");
      setStep("preview");
    }
  };

  const toggleSelect = (i) => {
    setSelectedIds(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
    );
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f9fafb" }}>
      <Navbar />

      {/* Banner */}
      <div style={{
        background: "linear-gradient(135deg, #0f172a 0%, #0d9488 100%)",
        padding: "40px 20px", textAlign: "center", color: "white"
      }}>
        <div style={{ fontSize: "40px", marginBottom: "8px" }}>🌐</div>
        <h1 style={{ fontSize: "28px", fontWeight: "800", margin: "0 0 8px" }}>
          Importer depuis votre site web
        </h1>
        <p style={{ fontSize: "15px", opacity: 0.85, margin: 0, maxWidth: "560px", marginLeft: "auto", marginRight: "auto" }}>
          Collez l'URL de votre site — l'IA analyse votre activité et crée automatiquement
          vos annonces classifiées selon les catégories MarinaDZ.
        </p>
      </div>

      <div style={{ flex: 1, maxWidth: "860px", margin: "0 auto", padding: "32px 20px", width: "100%" }}>

        {/* ── ÉTAPE : Saisie URL ── */}
        {(step === "url" || step === "analyzing") && (
          <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "32px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "32px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937", marginBottom: "8px" }}>
              1. Entrez l'URL de votre site
            </h2>
            <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px" }}>
              Peut être votre page d'accueil, la page produits ou services — l'IA s'adapte.
            </p>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <input
                type="url"
                placeholder="https://monentreprise.dz"
                value={url}
                onChange={e => { setUrl(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && step === "url" && handleAnalyze()}
                disabled={step === "analyzing"}
                style={{
                  flex: 1, minWidth: "220px", padding: "12px 16px", fontSize: "15px",
                  border: "2px solid #e5e7eb", borderRadius: "10px", outline: "none",
                  transition: "border-color .2s",
                }}
                onFocus={e => { e.target.style.borderColor = "#0d9488"; }}
                onBlur={e  => { e.target.style.borderColor = "#e5e7eb"; }}
              />
              <button
                onClick={handleAnalyze}
                disabled={step === "analyzing"}
                style={{
                  padding: "12px 28px", backgroundColor: step === "analyzing" ? "#9ca3af" : "#0d9488",
                  color: "white", border: "none", borderRadius: "10px", fontSize: "15px",
                  fontWeight: "700", cursor: step === "analyzing" ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {step === "analyzing" ? (
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ display: "inline-block", width: "14px", height: "14px", border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin .8s linear infinite" }} />
                    Analyse en cours…
                  </span>
                ) : "✨ Analyser le site"}
              </button>
            </div>

            {error && (
              <div style={{ marginTop: "12px", padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ marginTop: "20px", backgroundColor: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: "10px", padding: "14px 16px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#0d9488", marginBottom: "8px" }}>💡 COMMENT ÇA MARCHE</p>
              <ol style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#374151", lineHeight: "1.8" }}>
                <li>Collez l'URL de votre site ou d'une page produits</li>
                <li>L'IA analyse le contenu et propose des annonces adaptées à MarinaDZ</li>
                <li>Vous choisissez les annonces à publier — elles partent en validation admin</li>
              </ol>
            </div>
          </div>
        )}

        {/* ── ÉTAPE : Aperçu des annonces ── */}
        {step === "preview" && analysisResult && (
          <div>
            {/* Résumé de l'analyse */}
            <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "20px", borderLeft: "4px solid #0d9488" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                <div>
                  <p style={{ fontSize: "12px", color: "#0d9488", fontWeight: "700", textTransform: "uppercase", marginBottom: "4px" }}>✅ Site analysé avec succès</p>
                  {analysisResult.entreprise_detectee && (
                    <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937", margin: "0 0 4px" }}>
                      {analysisResult.entreprise_detectee}
                    </h3>
                  )}
                  {analysisResult.activite_principale && (
                    <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>{analysisResult.activite_principale}</p>
                  )}
                </div>
                <button onClick={() => { setStep("url"); setAnalysisResult(null); setSelectedIds([]); setError(""); }}
                  style={{ fontSize: "13px", color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "6px 12px", cursor: "pointer" }}>
                  ← Changer d'URL
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
              <div>
                <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
                  2. Sélectionnez les annonces à publier
                </h2>
                <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 0" }}>
                  {selectedIds.length} / {analysisResult.annonces.length} sélectionnée(s)
                </p>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setSelectedIds(analysisResult.annonces.map((_,i)=>i))}
                  style={{ fontSize: "12px", color: "#0d9488", background: "none", border: "1px solid #0d9488", borderRadius: "6px", padding: "5px 12px", cursor: "pointer" }}>
                  Tout sélectionner
                </button>
                <button onClick={() => setSelectedIds([])}
                  style={{ fontSize: "12px", color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "5px 12px", cursor: "pointer" }}>
                  Tout désélectionner
                </button>
              </div>
            </div>

            {error && (
              <div style={{ marginBottom: "12px", padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}>
                ⚠️ {error}
              </div>
            )}

            {/* Cards des annonces */}
            <div style={{ display: "grid", gap: "14px", marginBottom: "24px" }}>
              {analysisResult.annonces.map((a, i) => {
                const isSelected = selectedIds.includes(i);
                return (
                  <div key={i}
                    onClick={() => toggleSelect(i)}
                    style={{
                      backgroundColor: "white", borderRadius: "12px", padding: "20px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      border: isSelected ? "2px solid #0d9488" : "2px solid transparent",
                      cursor: "pointer", transition: "border-color .15s, box-shadow .15s",
                      outline: "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                      {/* Checkbox */}
                      <div style={{
                        width: "22px", height: "22px", borderRadius: "6px", flexShrink: 0, marginTop: "2px",
                        border: isSelected ? "none" : "2px solid #d1d5db",
                        backgroundColor: isSelected ? "#0d9488" : "white",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all .15s",
                      }}>
                        {isSelected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
                          <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", margin: 0 }}>{a.nom}</h3>
                          <span style={{ backgroundColor: "#f0fdfa", color: "#0d9488", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700" }}>
                            {CATEGORIES_LABELS[a.categorie] || a.categorie}
                          </span>
                          {a.sous_categorie && (
                            <span style={{ backgroundColor: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: "20px", fontSize: "11px" }}>
                              {a.sous_categorie}
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: "13px", color: "#4b5563", lineHeight: "1.6", margin: "0 0 8px" }}>
                          {a.description}
                        </p>
                        {a.tags?.length > 0 && (
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "10px" }}>
                            {a.tags.map((t, ti) => (
                              <span key={ti} style={{ backgroundColor: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: "20px", fontSize: "11px" }}>
                                #{t}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Images scrapées */}
                        {a.images_scrapees?.length > 0 && (
                          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
                            {a.images_scrapees.map((imgUrl, ii) => (
                              <img key={ii} src={imgUrl} alt=""
                                style={{ width: "72px", height: "56px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e5e7eb" }}
                                onError={e => { e.target.style.display = "none"; }}
                              />
                            ))}
                            <span style={{ fontSize: "11px", color: "#9ca3af", alignSelf: "center" }}>
                              {a.images_scrapees.length} photo{a.images_scrapees.length > 1 ? "s" : ""} importée{a.images_scrapees.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                        {(!a.images_scrapees || a.images_scrapees.length === 0) && (
                          <p style={{ fontSize: "11px", color: "#d1d5db", margin: "6px 0 0" }}>📷 Aucune photo trouvée sur cette page</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bouton confirmer */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={handleConfirm}
                disabled={selectedIds.length === 0}
                style={{
                  padding: "13px 36px", backgroundColor: selectedIds.length === 0 ? "#9ca3af" : "#0d9488",
                  color: "white", border: "none", borderRadius: "10px", fontSize: "15px",
                  fontWeight: "700", cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                Publier {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}annonce{selectedIds.length !== 1 ? "s" : ""} →
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE : Confirmation en cours ── */}
        {step === "confirming" && (
          <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "48px 32px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", marginBottom: "8px" }}>Création des annonces…</h2>
            <p style={{ fontSize: "14px", color: "#6b7280" }}>Cela prend quelques secondes.</p>
          </div>
        )}

        {/* ── ÉTAPE : Terminé ── */}
        {step === "done" && (
          <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "48px 32px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "32px" }}>
            <div style={{ fontSize: "56px", marginBottom: "16px" }}>🎉</div>
            <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#1f2937", marginBottom: "8px" }}>
              {createdCount} annonce{createdCount !== 1 ? "s" : ""} créée{createdCount !== 1 ? "s" : ""} !
            </h2>
            <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "28px", lineHeight: "1.6" }}>
              Vos annonces sont soumises pour validation par l'équipe MarinaDZ.<br />
              Vous serez notifié par email dès leur approbation.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/mes-annonces"
                style={{ padding: "12px 24px", backgroundColor: "#0d9488", color: "white", borderRadius: "10px", textDecoration: "none", fontSize: "14px", fontWeight: "700" }}>
                Voir mes annonces
              </Link>
              <button onClick={() => { setStep("url"); setUrl(""); setAnalysisResult(null); setSelectedIds([]); setError(""); setCreatedCount(0); }}
                style={{ padding: "12px 24px", backgroundColor: "white", color: "#0d9488", border: "2px solid #0d9488", borderRadius: "10px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
                Importer un autre site
              </button>
            </div>
          </div>
        )}

        {/* ── Historique des importations ── */}
        {!histLoading && history.length > 0 && (
          <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", marginBottom: "16px" }}>📋 Historique des importations</h3>
            <div style={{ display: "grid", gap: "10px" }}>
              {history.map(h => (
                <div key={h.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", backgroundColor: "#f9fafb", borderRadius: "8px", flexWrap: "wrap", gap: "8px" }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: "13px", fontWeight: "600", color: "#1f2937", margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "400px" }}>
                      {h.page_title || h.url}
                    </p>
                    <p style={{ fontSize: "11px", color: "#9ca3af", margin: 0 }}>
                      {new Date(h.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {h.status === "success" && (
                      <span style={{ backgroundColor: "#f0fdf4", color: "#16a34a", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700" }}>
                        ✅ {h.annonces_creees} annonce{h.annonces_creees !== 1 ? "s" : ""}
                      </span>
                    )}
                    {h.status === "analyzed" && (
                      <span style={{ backgroundColor: "#fefce8", color: "#ca8a04", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700" }}>
                        🔍 Analysé
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CSS keyframe pour le spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <Footer />
    </div>
  );
}

export default ImportSiteWeb;
